'use client';

import { useTranslations } from 'next-intl';
import { cn } from '@/shared/lib/cn';
import { usePackCheckout } from '@/features/payment/hooks/use-pack-checkout';
import { DirectToAppPurchase } from './DirectToAppPurchase';

/**
 * Facade Kit buy CTA. When the Paddle web checkout is configured
 * ({@link usePackCheckout} → `available`), this is a real in-page purchase: a
 * teal capsule that mints the correlation intent and opens the Paddle overlay,
 * bound to the building. When Paddle is not available (flag off / no client
 * token / no building), it degrades to the existing direct-to-app store
 * handoff ({@link DirectToAppPurchase}) — so web stays functional everywhere.
 *
 * The webhook owns the grant; this only launches checkout (API-driven state).
 */
export function PackBuyCta({
  projectId,
  verbLabel,
  calm = false,
  className,
  onStoreTap,
  onLaunch,
  onPurchased,
  onClosed,
}: {
  /** Building the Kit binds to. Required for the Paddle path. */
  projectId: string | undefined;
  /** Verb CTA lead (e.g. "Get everything for "<name>""). Falls back to a
   *  generic localized buy label when unset. */
  verbLabel?: string;
  /** Calmer, outlined capsule for the repeat "Additional Facade Kit" buy. */
  calm?: boolean;
  className?: string;
  /** Funnel hook for the store-handoff fallback (mobile path). */
  onStoreTap?: () => void;
  /**
   * Fired the instant the user taps buy, BEFORE the overlay opens. The paywall
   * uses it to relax its modal Radix `Dialog` to non-modal: Paddle's overlay
   * iframe is injected at `<body>` level, but a modal Dialog's
   * `react-remove-scroll` puts `pointer-events: none` on `<body>` + a focus
   * trap — which makes the body-level Paddle iframe render but stay unclickable.
   * Relaxing the lock (instead of closing the dialog) keeps the paywall mounted
   * so it can show the post-purchase success in place. On the storefront page
   * there is no host dialog, so this is unset.
   */
  onLaunch?: () => void;
  /** Fired on checkout.completed — the host flips to its success celebration. */
  onPurchased?: () => void;
  /** Fired on checkout.closed (purchase OR cancel) — the host re-locks. */
  onClosed?: () => void;
}) {
  const t = useTranslations('packs');
  const { available, isLaunching, startCheckout } = usePackCheckout(projectId, {
    onPurchased,
    onClosed,
  });

  if (!available) {
    return (
      <DirectToAppPurchase
        className={className}
        onStoreTap={onStoreTap}
        verbLabel={verbLabel}
        calm={calm}
      />
    );
  }

  return (
    <div className={cn('flex w-full flex-col items-center gap-3', className)}>
      <button
        type="button"
        onClick={() => {
          // Relax the host paywall's modal body-lock FIRST so Paddle's
          // body-level overlay is clickable, then launch (see onLaunch docs).
          onLaunch?.();
          void startCheckout();
        }}
        disabled={isLaunching}
        className={cn(
          // min-h + wrapping (not fixed height) so a long building name + long
          // currency can break to a second line instead of clipping.
          'inline-flex min-h-11 items-center justify-center whitespace-normal rounded-full px-6 py-2 text-center text-list-item-value font-semibold transition-opacity hover:opacity-90 disabled:opacity-60',
          calm
            ? 'border border-primary text-primary hover:bg-primary/5'
            : 'bg-primary text-primary-foreground'
        )}
      >
        {isLaunching
          ? t('checkoutOpening')
          : (verbLabel ?? t('getOneTimePack'))}
      </button>
    </div>
  );
}
